import { assessCompleteness, fragrances, getAllFeatureKeys } from '../fragrance/catalogue';
import {
  deriveFragranceSignalProfile,
  getFragranceSignal,
  type FragranceSignalId,
} from '../fragrance/signals';
import type { Confidence, FragranceRecord } from '../fragrance/types';
import type { Preferences } from '../preferences';
import { getVisualStyleLabel } from '../style-labels/vocabulary';
import type { StyleProfile } from '../style/schema';
import { fragranceSignalsFromStyle } from './visual-signal-map';

export type RecommendationRole =
  | 'Most like you'
  | 'Completes your style'
  | 'Unexpected contrast'
  | 'Quiet everyday choice'
  | 'Boldest expression';

export interface ScoreBreakdown {
  styleMatch: number;
  preferenceMatch: number;
  contextMatch: number;
  feedbackMatch: number;
  evidenceQuality: number;
  exclusionPenalty: number;
  finalScore: number;
  reasons: string[];
  uncertainties: string[];
  matchedSignalIds: FragranceSignalId[];
}

export interface Recommendation {
  role: RecommendationRole;
  fragrance: FragranceRecord;
  score: ScoreBreakdown;
  why: string;
  supportingFact: string;
  drawback: string;
  copySource: 'deterministic' | 'ai';
  copyVersion: string;
}

export interface RecommendationInput {
  style: StyleProfile;
  preferences: Preferences;
  feedbackWeights?: Record<string, number>;
  testedIds?: string[];
  refined?: boolean;
}

const COPY_VERSION = 'recommendation-copy-v2';
const normalise = (value: string) =>
  value
    .toLocaleLowerCase()
    .trim()
    .replace(/[\s-]+/g, '_');
const confidencePoints: Record<Confidence, number> = { high: 3, medium: 2, low: 0.5, unknown: 0 };
const mappingConfidence = { low: 0.72, medium: 1 };

const hasHardExclusion = (record: FragranceRecord, input: RecommendationInput) => {
  if (input.preferences.excludedIds.includes(record.fragrance_id)) return true;
  if (input.testedIds?.includes(record.fragrance_id)) return true;
  if (
    input.preferences.marketedGender !== 'any' &&
    record.identity.marketed_gender !== input.preferences.marketedGender
  ) {
    return true;
  }
  const features = getAllFeatureKeys(record);
  return input.preferences.dislikedNotes.some((note) => features.has(normalise(note)));
};

export const scoreFragrance = (
  record: FragranceRecord,
  input: RecommendationInput,
): ScoreBreakdown => {
  const features = getAllFeatureKeys(record);
  const reasons: string[] = [];
  const uncertainties: string[] = [];
  let styleMatch = 0;
  let preferenceMatch = 0;
  let contextMatch = 0;
  let feedbackMatch = 0;
  const excluded = hasHardExclusion(record, input);
  const exclusionPenalty = excluded ? -10_000 : 0;

  const productSignals = deriveFragranceSignalProfile(record);
  const productById = new Map(productSignals.signals.map((signal) => [signal.signalId, signal]));
  const visualMappings = fragranceSignalsFromStyle(input.style);
  const matchedSignalIds = [
    ...new Set(
      visualMappings
        .filter((mapping) => productById.has(mapping.signalId))
        .map((mapping) => mapping.signalId),
    ),
  ];
  for (const mapping of visualMappings) {
    const productSignal = productById.get(mapping.signalId);
    if (!productSignal) continue;
    styleMatch +=
      mapping.weight * productSignal.strength * mappingConfidence[mapping.mappingConfidence] * 4.5;
  }
  if (matchedSignalIds.length) {
    reasons.push(
      `Visual hypothesis: ${matchedSignalIds
        .slice(0, 3)
        .map((id) => getFragranceSignal(id)?.label.toLocaleLowerCase())
        .filter(Boolean)
        .join(', ')}`,
    );
  } else {
    uncertainties.push('The visual-to-fragrance hypothesis has limited catalogue overlap.');
  }

  // Explicit preferences have greater influence than feedback, visual hypotheses or context.
  for (const accord of input.preferences.likedAccords) {
    if (features.has(normalise(accord))) {
      preferenceMatch += 8;
      reasons.push(`Matches your stated interest in ${accord}`);
    }
  }
  if (input.preferences.likedFragranceIds.includes(record.fragrance_id)) preferenceMatch += 10;

  // Structured test feedback is next in precedence.
  for (const [feature, weight] of Object.entries(input.feedbackWeights ?? {})) {
    if (features.has(normalise(feature))) feedbackMatch += weight * 5;
  }

  // Context remains a small heuristic and contributes only when catalogue fields are present.
  if (input.preferences.season !== 'any') {
    if (record.wearing_context.seasons.includes(input.preferences.season)) contextMatch += 2;
    else if (record.wearing_context.seasons.length === 0)
      uncertainties.push('Season suitability is not available in the source data.');
  }
  if (input.preferences.occasion !== 'any') {
    const occasion = input.preferences.occasion;
    if (record.wearing_context.occasions.some((item) => normalise(item).includes(occasion))) {
      contextMatch += 1.5;
    } else if (record.wearing_context.occasions.length === 0) {
      uncertainties.push('Occasion suitability is not available in the source data.');
    }
  }
  if (input.preferences.intensity !== 'any') {
    if (record.performance.intensity.category === input.preferences.intensity) contextMatch += 1.5;
    else if (!record.performance.intensity.category)
      uncertainties.push('Intensity is not verified for this fragrance.');
  }

  const evidenceQuality =
    confidencePoints[record.official_classification.confidence] + assessCompleteness(record) * 2;
  if (record.official_classification.confidence === 'unknown') {
    uncertainties.push('Classification confidence is unknown.');
  }
  if (!record.official_classification.family) {
    uncertainties.push('Fragrance family is not verified.');
  }
  const finalScore =
    preferenceMatch +
    feedbackMatch +
    styleMatch +
    contextMatch +
    evidenceQuality +
    exclusionPenalty;
  return {
    styleMatch,
    preferenceMatch,
    contextMatch,
    feedbackMatch,
    evidenceQuality,
    exclusionPenalty,
    finalScore,
    reasons,
    uncertainties,
    matchedSignalIds,
  };
};

const roles: RecommendationRole[] = [
  'Most like you',
  'Completes your style',
  'Unexpected contrast',
  'Quiet everyday choice',
  'Boldest expression',
];

const roleSignals: Record<Exclude<RecommendationRole, 'Most like you'>, FragranceSignalId[]> = {
  'Completes your style': ['soft_musk', 'polished_woods', 'soft_floral', 'fresh_aromatic'],
  'Unexpected contrast': ['abstract_synthetic', 'dark_fruit', 'smoky_resin', 'tropical_fruit'],
  'Quiet everyday choice': ['airy_freshness', 'clean_musk', 'bright_citrus', 'crisp_green'],
  'Boldest expression': ['oriental_warmth', 'oud_depth', 'leather_texture', 'gourmand_richness'],
};

const roleBoost = (record: FragranceRecord, role: RecommendationRole, exploration: number) => {
  if (role === 'Most like you') return 0;
  const profile = deriveFragranceSignalProfile(record);
  const signalStrength = new Map(profile.signals.map((item) => [item.signalId, item.strength]));
  const hits = roleSignals[role].reduce((sum, id) => sum + (signalStrength.get(id) ?? 0), 0);
  if (role === 'Unexpected contrast') return hits * (2.4 + exploration * 3.2);
  if (role === 'Boldest expression') return hits * 4.2;
  if (role === 'Quiet everyday choice') {
    const louder = ['oud_depth', 'smoky_resin', 'gourmand_richness'] as FragranceSignalId[];
    return hits * 4 - louder.reduce((sum, id) => sum + (signalStrength.get(id) ?? 0), 0) * 2;
  }
  return hits * 3.2;
};

const diversityPenalty = (record: FragranceRecord, selected: Recommendation[]) => {
  let penalty = 0;
  const selectedBrands = new Set(selected.map((item) => normalise(item.fragrance.identity.brand)));
  if (selectedBrands.has(normalise(record.identity.brand))) penalty += 14;
  const candidateAccords = new Set(
    record.official_classification.accords.slice(0, 2).map(normalise),
  );
  for (const choice of selected) {
    if (
      choice.fragrance.official_classification.accords
        .slice(0, 2)
        .some((accord) => candidateAccords.has(normalise(accord)))
    ) {
      penalty += 5;
    }
  }
  return penalty;
};

const stableHash = (value: string) => {
  let result = 2166136261;
  for (const character of value) result = Math.imul(result ^ character.charCodeAt(0), 16777619);
  return result >>> 0;
};

const fallbackDescription = (
  role: RecommendationRole,
  record: FragranceRecord,
  score: ScoreBreakdown,
  style: StyleProfile,
) => {
  const accords = record.official_classification.accords.slice(0, 3);
  const core = style.calibratedCoreLabelIds
    .slice(0, 2)
    .map((id) => getVisualStyleLabel(id)?.label)
    .filter(Boolean);
  const signals = score.matchedSignalIds
    .slice(0, 2)
    .map((id) => getFragranceSignal(id)?.label.toLocaleLowerCase())
    .filter(Boolean);
  const accordText = accords.length
    ? accords.join(', ')
    : (record.official_classification.family ?? 'its documented composition');
  const styleText = core.join(' and ') || 'your current visual references';
  const signalText = signals.join(' and ') || 'a lower-confidence visual connection';
  const templates = [
    `${record.identity.name} centres ${accordText}. Its ${signalText} gives the ${role.toLocaleLowerCase()} role a credible link to ${styleText}, while keeping the relationship an editorial hypothesis to test on skin.`,
    `Reported ${accordText} shapes ${record.identity.name}, with ${signalText} providing the bridge to ${styleText}. In the ${role.toLocaleLowerCase()} position, it offers a distinct direction rather than a claim about how it will wear.`,
    `${record.identity.name} brings together ${accordText}; the catalogue evidence points to ${signalText}. That combination interprets ${styleText} for the ${role.toLocaleLowerCase()} role, but sampling remains the useful final check.`,
    `Built around reported ${accordText}, ${record.identity.name} reads through ${signalText}. It answers ${styleText} from the ${role.toLocaleLowerCase()} angle without treating the visual mapping as a certainty.`,
  ];
  return templates[
    stableHash(`${record.fragrance_id}|${role}|${core.join('|')}`) % templates.length
  ]!;
};

const supportingFactFor = (record: FragranceRecord) => {
  const accords = record.official_classification.accords.slice(0, 3);
  if (accords.length) return `Reported accords: ${accords.join(', ')}.`;
  if (record.official_classification.family) {
    return `Reported family: ${record.official_classification.family}.`;
  }
  const notes = record.normalised_notes.slice(0, 3).map((note) => note.raw_name);
  return notes.length
    ? `Reported notes: ${notes.join(', ')}.`
    : 'Composition details are limited in the catalogue.';
};

const assembleRecommendation = (
  role: RecommendationRole,
  record: FragranceRecord,
  score: ScoreBreakdown,
  style: StyleProfile,
): Recommendation => ({
  role,
  fragrance: record,
  score,
  why: fallbackDescription(role, record, score, style),
  supportingFact: supportingFactFor(record),
  drawback:
    score.uncertainties[0] ??
    record.data_quality.warnings[0] ??
    'Skin wear can vary; sample before deciding.',
  copySource: 'deterministic',
  copyVersion: COPY_VERSION,
});

export const recommendFragrances = (
  input: RecommendationInput,
  catalogue: FragranceRecord[] = fragrances,
): Recommendation[] => {
  const scored = catalogue
    .map((fragrance) => ({ fragrance, score: scoreFragrance(fragrance, input) }))
    .filter(({ score }) => score.exclusionPenalty === 0)
    .sort(
      (left, right) =>
        right.score.finalScore - left.score.finalScore ||
        left.fragrance.fragrance_id.localeCompare(right.fragrance.fragrance_id),
    );
  const selected: Recommendation[] = [];
  for (const role of roles) {
    const candidates = scored
      .filter(
        ({ fragrance }) =>
          !selected.some((item) => item.fragrance.fragrance_id === fragrance.fragrance_id),
      )
      .map((candidate) => ({
        ...candidate,
        roleScore:
          candidate.score.finalScore +
          roleBoost(candidate.fragrance, role, input.preferences.exploration) -
          diversityPenalty(candidate.fragrance, selected),
      }))
      .sort(
        (left, right) =>
          right.roleScore - left.roleScore ||
          left.fragrance.fragrance_id.localeCompare(right.fragrance.fragrance_id),
      );
    const choice = candidates[0];
    if (choice) {
      selected.push(assembleRecommendation(role, choice.fragrance, choice.score, input.style));
    }
  }
  return selected;
};
